import { useInfiniteQuery } from '@tanstack/react-query'
import type {
  ApiFetch,
  TableFilters,
  Transaction,
  TransactionPageResponse,
  WorkerSchema,
} from '../types'
import { getDisplayValue } from '../utils/display'
import { CLIENT_ONLY_TABLE_FILTER_KEYS } from '../utils/similarNameChips'

const DEFAULT_TRANSACTION_REFETCH_INTERVAL_MS = 12_000

interface UseTransactionsOptions {
  apiFetch?: ApiFetch
  [key: string]: unknown
}

function toBangkokDateKey(input: unknown): string | null {
  if (!input) return null
  const raw = getDisplayValue(input)
  if (raw == null) return null
  const s = String(raw).trim()
  if (!s) return null
  const d = new Date(s)
  if (Number.isNaN(d.getTime())) return null
  // en-CA yields YYYY-MM-DD for easy compare
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Asia/Bangkok',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(d)
}

function inDateRangeBangkok(record: Transaction, from: string | null, to: string | null): boolean {
  if (!from && !to) return true
  const key = toBangkokDateKey(
    (record as any).created_at ?? (record as any).record_date ?? (record as any).recorded_at,
  )
  if (!key) return false
  if (from && key < from) return false
  if (to && key > to) return false
  return true
}

function toSortableNumber(value: unknown): number | null {
  const raw = getDisplayValue(value)
  if (raw == null) return null
  if (typeof raw === 'number') return Number.isFinite(raw) ? raw : null
  if (raw instanceof Date) return Number.isFinite(raw.getTime()) ? raw.getTime() : null
  const s = String(raw).trim()
  if (!s) return null
  const asNum = Number(s)
  if (Number.isFinite(asNum)) return asNum
  const ms = Date.parse(s)
  return Number.isFinite(ms) ? ms : null
}

function clientSort(records: Transaction[], filters: TableFilters): Transaction[] {
  const sortKey = filters.sort
  const dir = (filters.dir || 'asc').toLowerCase() === 'desc' ? -1 : 1
  if (!sortKey) return records

  const decorated = records.map((r, i) => ({ r, i }))
  decorated.sort((a, b) => {
    const av = (a.r as any)?.[sortKey]
    const bv = (b.r as any)?.[sortKey]
    const an = toSortableNumber(av)
    const bn = toSortableNumber(bv)
    if (an == null && bn == null) return a.i - b.i
    if (an == null) return 1
    if (bn == null) return -1
    if (an === bn) return a.i - b.i
    return (an < bn ? -1 : 1) * dir
  })
  return decorated.map((d) => d.r)
}

function normalizeRecord(raw: Record<string, unknown>): Transaction {
  const normalized = Object.fromEntries(
    Object.entries(raw).map(([key, value]) => [
      key,
      getDisplayValue(value),
    ]),
  ) as Record<string, unknown>
  if (normalized.created_at != null && normalized.recorded_at == null) {
    normalized.recorded_at = normalized.created_at
  }

  return normalized as Transaction
}

function buildTransactionsUrl(
  schema: WorkerSchema,
  workspaceId: string,
  filters: Record<string, string | null | undefined>,
  cursor?: string,
): string {
  const url = new URL(schema.apiEndpoint, 'http://localhost')
  const params = url.searchParams

  params.set('worker', schema.workerId)
  params.set('workspace_id', workspaceId)

  if (cursor) params.set('cursor', cursor)

  for (const [key, value] of Object.entries(filters)) {
    if (!value || key === 'worker' || key === 'workspaceId' || key === 'workspace_id') continue
    if (CLIENT_ONLY_TABLE_FILTER_KEYS.has(key)) continue
    // Legacy date sorts → created_at (เวลาสร้าง).
    if (
      key === 'sort' &&
      (value === 'recorded_at' || value === 'record_date') &&
      (schema.workerId === 'bill-lin-income' || schema.workerId === 'bill-lin-expense')
    ) {
      params.set('sort', 'created_at')
      continue
    }
    params.set(key, value)
  }

  // รายรับ/รายจ่าย "วันที่" + ฟิลเตอร์เดือน = created_at (เวลาสร้าง).
  if (
    (schema.workerId === 'bill-lin-income' || schema.workerId === 'bill-lin-expense') &&
    !params.get('date_field') &&
    (params.get('from') || params.get('to'))
  ) {
    params.set('date_field', 'created_at')
  }

  return `${url.pathname}?${params.toString()}`
}

async function fetchTransactions(
  schema: WorkerSchema,
  workspaceId: string,
  filters: TableFilters,
  cursor: string | undefined,
  fetcher: ApiFetch,
): Promise<TransactionPageResponse> {
  const url = buildTransactionsUrl(schema, workspaceId, filters, cursor)

  const controller = new AbortController()
  const id = setTimeout(() => controller.abort(), 20_000)

  try {
    const tryFetch = async (
      fetchUrl: string,
    ): Promise<{ payload: TransactionPageResponse; relaxed: boolean }> => {
      const res = await fetcher(fetchUrl, { signal: controller.signal })

      if (!res.ok) {
        let bodyText: string | null = null
        try {
          bodyText = await res.text()
        } catch {
          bodyText = null
        }

        const error = new Error(`Failed to fetch transactions (${res.status})`) as Error & {
          status?: number
          url?: string
          body?: string
        }
        error.status = res.status
        error.url = fetchUrl
        if (bodyText) error.body = bodyText.slice(0, 800)

        if (typeof window !== 'undefined' && window.localStorage?.getItem?.('AERA_DT_DEBUG') === '1') {
          console.error('[AeraDataTable] fetchTransactions failed', {
            status: res.status,
            url: fetchUrl,
            filters,
            cursor,
            body: bodyText ? bodyText.slice(0, 800) : null,
          })
        }

        throw error
      }

      const relaxed = res.headers.get('x-aera-upstream-relaxed') === '1'
      const payload = (await res.json()) as TransactionPageResponse
      return { payload, relaxed }
    }

    let out: { payload: TransactionPageResponse; relaxed: boolean } | undefined
    try {
      out = await tryFetch(url)
    } catch (error) {
      const sort = filters?.sort
      if (!sort) throw error

      const debugEnabled = typeof window !== 'undefined' && window.localStorage?.getItem?.('AERA_DT_DEBUG') === '1'
      const candidates: string[] = []
      if (sort === 'record_date') candidates.push('recorded_at', 'updated_at', 'created_at', 'transfer_datetime')
      if (sort === 'recorded_at') candidates.push('record_date', 'updated_at', 'created_at', 'transfer_datetime')
      if (sort === 'updated_at') candidates.push('record_date', 'recorded_at', 'created_at', 'transfer_datetime')
      if (sort === 'created_at') candidates.push('record_date', 'updated_at', 'recorded_at', 'transfer_datetime')

      for (const candidate of candidates) {
        const retryUrl = buildTransactionsUrl(schema, workspaceId, { ...filters, sort: candidate }, cursor)
        if (debugEnabled) {
          console.info('[AeraDataTable] retrying fetchTransactions with sort fallback', {
            fromSort: sort,
            toSort: candidate,
            url,
            retryUrl,
          })
        }

        try {
          out = await tryFetch(retryUrl)
          break
        } catch {
          // Keep trying the remaining known sort fallbacks.
        }
      }

      if (!out) {
        const { sort: _sort, dir: _dir, ...relaxedFilters } = filters
        const relaxedUrl = buildTransactionsUrl(schema, workspaceId, relaxedFilters, cursor)
        if (debugEnabled) {
          console.info('[AeraDataTable] retrying fetchTransactions without sort', {
            url,
            relaxedUrl,
          })
        }
        out = await tryFetch(relaxedUrl)
      }
    }

    const payload = out!.payload
    const normalized = payload.data.map((record) =>
      normalizeRecord(record as Record<string, unknown>),
    )
    const filtered = out!.relaxed
      ? normalized.filter((r) => inDateRangeBangkok(r, filters.from, filters.to))
      : normalized
    const sorted = out!.relaxed ? clientSort(filtered, filters) : filtered

    return {
      ...payload,
      data: sorted,
      meta: payload.meta ?? {
        totalCount: payload.total ?? sorted.length,
        totalAmount: 0,
      },
    }
  } finally {
    clearTimeout(id)
  }
}

export function useTransactions(
  schema: WorkerSchema,
  workspaceId: string,
  filters: TableFilters,
  options?: UseTransactionsOptions,
) {
  const { apiFetch = fetch, ...queryOptions } = options ?? {}
  const serverFilters = Object.fromEntries(
    Object.entries(filters).filter(
      ([key]) => !CLIENT_ONLY_TABLE_FILTER_KEYS.has(key),
    ),
  ) as TableFilters

  return useInfiniteQuery({
    queryKey: ['transactions', schema.workerId, workspaceId, serverFilters],
    queryFn: ({ pageParam }: { pageParam: unknown }) =>
      fetchTransactions(
        schema,
        workspaceId,
        serverFilters,
        pageParam as string | undefined,
        apiFetch,
      ),
    initialPageParam: undefined,
    getNextPageParam: (lastPage: TransactionPageResponse) => lastPage.nextCursor ?? undefined,
    // Keep short so match/cancel on another tab is visible soon even if a
    // targeted resetQueries was skipped; poll still covers multi-tab drift.
    staleTime: 5_000,
    gcTime: 5 * 60_000,
    refetchInterval: DEFAULT_TRANSACTION_REFETCH_INTERVAL_MS,
    refetchIntervalInBackground: false,
    retry: (count: number, error: any) =>
      count < 2 && (error?.status >= 500 || error?.name === 'AbortError'),
    retryDelay: (attempt: number) => Math.min(1000 * 2 ** attempt, 10000),
    ...queryOptions,
    // After queryOptions so callers cannot silently re-enable paused refetch.
    // LINE/LIFF WebViews often fire false `offline` events; default
    // networkMode:'online' then makes refetch() a silent no-op on Retry.
    networkMode: 'always',
  })
}
